import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import {
  GripVertical,
  ChevronDown,
  ChevronRight,
  Scissors,
  Link2,
  FileText
} from 'lucide-react';
import { ReportSection } from '../../hooks/usePdfExportState';

interface SectionControlPanelProps {
  sections: ReportSection[];
  selectedSections: Record<string, boolean>;
  sectionOrder: string[];
  manualPageBreaks: Set<string>;
  keepTogether: Set<string>;
  sectionToPage: Map<string, number>;
  onToggleSection: (sectionId: string) => void;
  onToggleAll: (selected: boolean) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onTogglePageBreak: (sectionId: string) => void;
  onToggleKeepTogether: (sectionId: string) => void;
  // Colored sub-blocks (kind: 'block') are controlled via a DOM overlay, not
  // selectedSections. A block is visible when its id is NOT in removedBlocks.
  removedBlocks: Set<string>;
  onToggleBlock: (blockId: string) => void;
}

export const SectionControlPanel: React.FC<SectionControlPanelProps> = ({
  sections,
  selectedSections,
  sectionOrder,
  manualPageBreaks,
  keepTogether,
  sectionToPage,
  onToggleSection,
  onToggleAll,
  onReorder,
  onTogglePageBreak,
  onToggleKeepTogether,
  removedBlocks,
  onToggleBlock
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Get parent sections (non-child sections)
  const parentSections = sections.filter(s => !s.isChild);

  // Get children for a parent section
  const getChildren = (parentId: string) =>
    sections.filter(s => s.parentId === parentId);

  // Toggle section expansion
  const toggleExpand = (sectionId: string) => {
    setExpandedSections(prev => {
      const updated = new Set(prev);
      if (updated.has(sectionId)) {
        updated.delete(sectionId);
      } else {
        updated.add(sectionId);
      }
      return updated;
    });
  };

  // Handle drag end
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const fromIndex = result.source.index;
    const toIndex = result.destination.index;

    if (fromIndex !== toIndex) {
      onReorder(fromIndex, toIndex);
    }
  };

  // Count selected items. Real sections live in selectedSections; colored
  // blocks are "selected" when not present in removedBlocks.
  const visibleBlockCount = sections.filter(
    s => s.kind === 'block' && !removedBlocks.has(s.id)
  ).length;
  const selectedCount = Object.values(selectedSections).filter(Boolean).length + visibleBlockCount;
  const totalCount = sections.length;

  // Order sections based on sectionOrder
  const orderedParentSections = sectionOrder
    .map(id => parentSections.find(s => s.id === id))
    .filter((s): s is ReportSection => s !== undefined && !s.isChild);

  return (
    <div className="section-control-panel h-full flex flex-col bg-white">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">
            Sections
          </h2>
          <span className="text-sm text-gray-500">
            {selectedCount}/{totalCount}
          </span>
        </div>

        {/* Select All / None buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => onToggleAll(true)}
            className="flex-1 px-3 py-1.5 text-sm text-[#1993e5] bg-blue-50 hover:bg-blue-100 rounded transition-colors"
          >
            Select All
          </button>
          <button
            onClick={() => onToggleAll(false)}
            className="flex-1 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
          >
            Select None
          </button>
        </div>
      </div>

      {/* Sections list with drag-drop */}
      <div className="flex-1 overflow-y-auto p-2">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="sections-list">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`space-y-1 ${snapshot.isDraggingOver ? 'bg-blue-50 rounded-lg' : ''}`}
              >
                {orderedParentSections.map((section, index) => {
                  const children = getChildren(section.id);
                  const hasChildren = children.length > 0;
                  const isExpanded = expandedSections.has(section.id);
                  const isSelected = selectedSections[section.id];
                  const hasPageBreak = manualPageBreaks.has(section.id);
                  const isKeptTogether = keepTogether.has(section.id);
                  const pageNum = sectionToPage.get(section.id);

                  return (
                    <Draggable
                      key={section.id}
                      draggableId={section.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`section-item ${snapshot.isDragging ? 'shadow-lg ring-2 ring-[#1993e5] bg-white' : ''}`}
                        >
                          {/* Parent section row */}
                          <div
                            className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                              isSelected ? 'bg-gray-50' : 'bg-gray-100 opacity-60'
                            } hover:bg-gray-100`}
                          >
                            {/* Drag handle */}
                            <div
                              {...provided.dragHandleProps}
                              className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                            >
                              <GripVertical className="w-4 h-4" />
                            </div>

                            {/* Expand toggle for sections with children */}
                            {hasChildren ? (
                              <button
                                onClick={() => toggleExpand(section.id)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </button>
                            ) : (
                              <div className="w-4" /> // Spacer
                            )}

                            {/* Checkbox */}
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => onToggleSection(section.id)}
                              className="h-4 w-4 text-[#1993e5] border-gray-300 rounded focus:ring-[#1993e5]"
                            />

                            {/* Section title */}
                            <span className={`flex-1 text-sm truncate ${
                              isSelected ? 'text-gray-900 font-medium' : 'text-gray-500'
                            }`}>
                              {section.title}
                            </span>

                            {/* Page indicator */}
                            {pageNum && (
                              <span className="text-xs text-gray-400 px-1.5 py-0.5 bg-gray-100 rounded">
                                p.{pageNum}
                              </span>
                            )}

                            {/* Page break toggle */}
                            <button
                              onClick={() => onTogglePageBreak(section.id)}
                              className={`p-1 rounded transition-colors ${
                                hasPageBreak
                                  ? 'text-orange-500 bg-orange-50 hover:bg-orange-100'
                                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                              }`}
                              title={hasPageBreak ? 'Remove page break after this section' : 'Add page break after this section'}
                            >
                              <Scissors className="w-3.5 h-3.5" />
                            </button>

                            {/* Keep together toggle */}
                            <button
                              onClick={() => onToggleKeepTogether(section.id)}
                              className={`p-1 rounded transition-colors ${
                                isKeptTogether
                                  ? 'text-green-500 bg-green-50 hover:bg-green-100'
                                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                              }`}
                              title={isKeptTogether ? 'Allow this section to split across pages' : 'Keep this section together on one page'}
                            >
                              <Link2 className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* Page break indicator */}
                          {hasPageBreak && (
                            <div className="flex items-center gap-2 px-4 py-1 text-xs text-orange-600">
                              <div className="flex-1 border-t border-dashed border-orange-300" />
                              <span>Page Break</span>
                              <div className="flex-1 border-t border-dashed border-orange-300" />
                            </div>
                          )}

                          {/* Children sections (when expanded) */}
                          {hasChildren && isExpanded && (
                            <div className="ml-8 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
                              {children.map((child) => {
                                const isBlock = child.kind === 'block';
                                // Blocks: visible unless removed. Sections: selectedSections.
                                const childSelected = isBlock
                                  ? !removedBlocks.has(child.id)
                                  : selectedSections[child.id];
                                const childHasPageBreak = manualPageBreaks.has(child.id);
                                const childKeptTogether = keepTogether.has(child.id);
                                const childPageNum = sectionToPage.get(child.id);

                                return (
                                  <div key={child.id}>
                                    <div
                                      className={`flex items-center gap-2 p-1.5 rounded transition-colors ${
                                        childSelected ? 'bg-gray-50' : 'bg-gray-100 opacity-60'
                                      } hover:bg-gray-100`}
                                    >
                                      {/* Child icon */}
                                      <FileText className="w-3.5 h-3.5 text-gray-400" />

                                      {/* Checkbox */}
                                      <input
                                        type="checkbox"
                                        checked={childSelected}
                                        onChange={() => isBlock ? onToggleBlock(child.id) : onToggleSection(child.id)}
                                        className="h-3.5 w-3.5 text-[#1993e5] border-gray-300 rounded focus:ring-[#1993e5]"
                                      />

                                      {/* Title */}
                                      <span className={`flex-1 text-xs truncate ${
                                        childSelected ? 'text-gray-700' : 'text-gray-500'
                                      }`}>
                                        {child.title}
                                      </span>

                                      {/* Page indicator */}
                                      {childPageNum && (
                                        <span className="text-[10px] text-gray-400 px-1 py-0.5 bg-gray-100 rounded">
                                          p.{childPageNum}
                                        </span>
                                      )}

                                      {/* Page break / keep-together apply to real sections only.
                                          Colored blocks are layout-agnostic (removal only). */}
                                      {!isBlock && (
                                        <>
                                          <button
                                            onClick={() => onTogglePageBreak(child.id)}
                                            className={`p-0.5 rounded transition-colors ${
                                              childHasPageBreak
                                                ? 'text-orange-500 bg-orange-50'
                                                : 'text-gray-400 hover:text-gray-600'
                                            }`}
                                            title="Toggle page break"
                                          >
                                            <Scissors className="w-3 h-3" />
                                          </button>

                                          <button
                                            onClick={() => onToggleKeepTogether(child.id)}
                                            className={`p-0.5 rounded transition-colors ${
                                              childKeptTogether
                                                ? 'text-green-500 bg-green-50'
                                                : 'text-gray-400 hover:text-gray-600'
                                            }`}
                                            title="Toggle keep together"
                                          >
                                            <Link2 className="w-3 h-3" />
                                          </button>
                                        </>
                                      )}
                                    </div>

                                    {/* Child page break indicator */}
                                    {!isBlock && childHasPageBreak && (
                                      <div className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-orange-600">
                                        <div className="flex-1 border-t border-dashed border-orange-300" />
                                        <span>Break</span>
                                        <div className="flex-1 border-t border-dashed border-orange-300" />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* Legend */}
      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <h3 className="text-xs font-medium text-gray-700 mb-2">Legend</h3>
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
          <div className="flex items-center gap-1.5">
            <GripVertical className="w-3 h-3 text-gray-400" />
            <span>Drag to reorder</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Scissors className="w-3 h-3 text-orange-500" />
            <span>Page break after</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Link2 className="w-3 h-3 text-green-500" />
            <span>Keep together</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] px-1 py-0.5 bg-gray-200 rounded">p.1</span>
            <span>Page number</span>
          </div>
        </div>
      </div>

      {/* Styles */}
      <style>{`
        .section-control-panel {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
        }

        .section-control-panel::-webkit-scrollbar {
          width: 6px;
        }

        .section-control-panel::-webkit-scrollbar-track {
          background: #f1f5f9;
        }

        .section-control-panel::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        .section-item {
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }

        .section-item[style*="transform"] {
          z-index: 100;
        }
      `}</style>
    </div>
  );
};

export default SectionControlPanel;
